import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Leaf,
  Package,
  Phone,
  Sparkles,
  Sprout,
} from 'lucide-react';
import { fetchAllProducts, fetchCategories } from '../api/catalog';
import {
  PRODUCT_HUB_INITIAL_COUNT,
  PRODUCT_HUB_LOAD_COUNT,
} from '../data/productCatalog';
import {
  groupProductsByCategoryId,
  mapCategoryToHubItem,
} from '../utils/catalogDisplay';

const CATEGORY_ICONS = {
  wheat: Sprout,
  flame: Sparkles,
  droplets: Sparkles,
  circle: Package,
  bean: Sprout,
  sprout: Sprout,
  nut: Package,
  sparkles: Sparkles,
  candy: Sparkles,
  layers: Package,
  utensils: Package,
  jar: Package,
  disc: Package,
  cookie: Package,
  leaf: Leaf,
  flower: Leaf,
  package: Package,
};

function ProductsHub() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visibleCount, setVisibleCount] = useState(PRODUCT_HUB_INITIAL_COUNT);

  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      setLoading(true);
      setError('');

      try {
        const [categoriesData, productRows] = await Promise.all([
          fetchCategories(),
          fetchAllProducts(),
        ]);
        if (cancelled) return;

        const productsByCategoryId = groupProductsByCategoryId(productRows);
        const mapped = categoriesData
          .map((category) => mapCategoryToHubItem({
            ...category,
            products: productsByCategoryId[category.id] || [],
          }))
          .filter((category) => category.productCount > 0);

        setCategories(mapped);
        setVisibleCount(Math.min(PRODUCT_HUB_INITIAL_COUNT, mapped.length || PRODUCT_HUB_INITIAL_COUNT));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load product categories.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleCategories = categories.slice(0, visibleCount);
  const remainingCount = categories.length - visibleCount;

  const loadMore = () => {
    setVisibleCount((count) =>
      Math.min(count + PRODUCT_HUB_LOAD_COUNT, categories.length),
    );
  };

  return (
    <>
      <section className="product-range">
        <div className="product-list">
          <div className="section-title reveal">
            <Leaf size={14} strokeWidth={1.75} />
            <h2>Explore Our Range</h2>
          </div>

          {loading && (
            <p className="product-status reveal">Loading product categories…</p>
          )}

          {!loading && error && (
            <p className="product-status product-status--error reveal">{error}</p>
          )}

          {!loading && !error && categories.length === 0 && (
            <p className="product-status reveal">No product categories available yet.</p>
          )}

          {visibleCategories.map((cat, index) => {
            const Icon = CATEGORY_ICONS[cat.icon] || Leaf;
            const reverse = index % 2 === 1;

            return (
              <article
                key={cat.id}
                className={`product-item reveal ${reverse ? 'reverse' : ''}`}
              >
                <div className="product-item-image">
                  <img src={cat.image} alt={cat.label} loading="lazy" />
                </div>
                <div className="product-item-content">
                  <span className="product-item-icon">
                    <Icon size={26} strokeWidth={1.5} />
                  </span>
                  <h3>{cat.label}</h3>
                  <p>{cat.desc}</p>
                  <Link
                    to={`/products/category/${cat.slug}`}
                    className="product-item-link"
                  >
                    Explore Products
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            );
          })}

          {remainingCount > 0 && (
            <div className="product-load-wrap reveal">
              <button
                type="button"
                className="product-load-more"
                onClick={loadMore}
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="hub-quote">
        <div className="hub-quote-wrap">
          <div className="hub-quote-box reveal">
            <div className="hub-quote-grain" aria-hidden="true" />
            <div className="hub-quote-blob" aria-hidden="true" />
            <div className="hub-quote-content">
              <div className="hub-quote-text">
                <span className="hub-quote-eyebrow">India → United Kingdom</span>
                <h2>Importing to the UK? Get a wholesale quote.</h2>
                <p>
                  Send your product list and quantities — we&apos;ll reply with trade
                  pricing, packing options, freight routes, and full export paperwork.
                </p>
              </div>
              <div className="hub-quote-actions">
                <Link to="/contact" className="hub-quote-btn">
                  Request UK Import Quote
                  <ArrowRight size={16} />
                </Link>
                <a href="tel:+914422345678" className="hub-quote-link">
                  <Phone size={16} />
                  +91 442 234 5678
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ProductsHub;
